"use client";

import {
	createContext,
	type ReactNode,
	useCallback,
	useContext,
	useEffect,
	useState,
} from "react";
import { trpcClient } from "@/integrations/tanstack-query/root-provider";

export type UserRole = "owner" | "admin" | "viewer";

export interface User {
	id: string;
	email: string;
	firstName: string;
	lastName: string;
	role: UserRole;
	organizationId: string;
	organizationName: string;
}

interface AuthContextType {
	user: User | null;
	isAuthenticated: boolean;
	isLoading: boolean;
	login: (email: string, password: string) => Promise<boolean>;
	register: (data: {
		firstName: string;
		lastName: string;
		email: string;
		password: string;
		orgName: string;
	}) => Promise<{ success: boolean; needsEmailConfirmation: boolean }>;
	logout: () => Promise<void>;
	hasPermission: (permission: "view" | "edit" | "admin" | "owner") => boolean;
}

const AuthContext = createContext<AuthContextType | null>(null);

// Lazy-load supabase client only on the client side
async function getSupabase() {
	if (typeof window === "undefined") return null;
	const { supabase } = await import("@/lib/supabase-browser");
	return supabase;
}

export function AuthProvider({ children }: { children: ReactNode }) {
	const [user, setUser] = useState<User | null>(null);
	const [isLoading, setIsLoading] = useState(true);

	// Fetch user profile and org info
	const fetchUserProfile = useCallback(async (accessToken: string) => {
		try {
			const response = await fetch("/api/trpc/auth.me", {
				method: "GET",
				headers: {
					Authorization: `Bearer ${accessToken}`,
					"Content-Type": "application/json",
				},
			});

			if (!response.ok) {
				console.error("Failed to fetch user profile");
				return null;
			}

			const data = await response.json();
			// tRPC with superjson returns { result: { data: { json: ... } } }
			const result = data.result?.data?.json ?? data.result?.data;

			if (!result) {
				console.error("No result from auth.me:", data);
				return null;
			}

			// Get the first membership (primary org)
			const primaryMembership = result.memberships?.[0];
			const org = primaryMembership?.organizations;

			const userData: User = {
				id: result.id,
				email: result.email,
				firstName: result.firstName,
				lastName: result.lastName,
				role: primaryMembership?.role ?? "viewer",
				organizationId: org?.id ?? "",
				organizationName: org?.name ?? "",
			};

			return userData;
		} catch (error) {
			console.error("Error fetching user profile:", error);
			return null;
		}
	}, []);

	// Check for existing session on mount
	useEffect(() => {
		let subscription: { unsubscribe: () => void } | null = null;

		const initAuth = async () => {
			const supabase = await getSupabase();
			if (!supabase) {
				setIsLoading(false);
				return;
			}

			try {
				const {
					data: { session },
				} = await supabase.auth.getSession();

				if (session?.access_token) {
					const userData = await fetchUserProfile(session.access_token);
					if (userData) {
						setUser(userData);
					}
				}

				// Subscribe to auth state changes
				const { data } = supabase.auth.onAuthStateChange(
					async (event, session) => {
						if (event === "SIGNED_OUT") {
							setUser(null);
						} else if (session?.access_token && event === "SIGNED_IN") {
							const userData = await fetchUserProfile(session.access_token);
							if (userData) {
								setUser(userData);
							}
						}
					},
				);
				subscription = data.subscription;
			} catch (error) {
				console.error("Error initializing auth:", error);
			} finally {
				setIsLoading(false);
			}
		};

		initAuth();

		return () => {
			subscription?.unsubscribe();
		};
	}, [fetchUserProfile]);

	const login = async (email: string, password: string): Promise<boolean> => {
		try {
			const result = await trpcClient.auth.login.mutate({ email, password });

			if (result.session) {
				// Set the session on the Supabase client
				const supabase = await getSupabase();
				if (supabase) {
					await supabase.auth.setSession({
						access_token: result.session.access_token,
						refresh_token: result.session.refresh_token,
					});
				}

				// Fetch full user profile
				const userData = await fetchUserProfile(result.session.access_token);
				if (userData) {
					setUser(userData);
					return true;
				}
			}
			return false;
		} catch (error) {
			console.error("Login error:", error);
			return false;
		}
	};

	const register = async (data: {
		firstName: string;
		lastName: string;
		email: string;
		password: string;
		orgName: string;
	}): Promise<{ success: boolean; needsEmailConfirmation: boolean }> => {
		try {
			const result = await trpcClient.auth.signup.mutate({
				firstName: data.firstName,
				lastName: data.lastName,
				email: data.email,
				password: data.password,
				orgName: data.orgName,
			});

			if (result.session) {
				// Set the session on the Supabase client
				const supabase = await getSupabase();
				if (supabase) {
					await supabase.auth.setSession({
						access_token: result.session.access_token,
						refresh_token: result.session.refresh_token,
					});
				}

				// Set user data from signup response
				const userData: User = {
					id: result.user.id,
					email: result.user.email ?? data.email,
					firstName: result.user.firstName,
					lastName: result.user.lastName,
					role: "owner", // Signup creates owner
					organizationId: result.organization.id,
					organizationName: result.organization.name,
				};
				setUser(userData);
				return { success: true, needsEmailConfirmation: false };
			}

			// User created but no session = email confirmation required
			return { success: true, needsEmailConfirmation: true };
		} catch (error) {
			console.error("Registration error:", error);
			throw error; // Re-throw so the form can handle specific errors
		}
	};

	const logout = async () => {
		try {
			const supabase = await getSupabase();
			if (supabase) {
				await supabase.auth.signOut();
			}
			setUser(null);
		} catch (error) {
			console.error("Logout error:", error);
			// Still clear user state even if API call fails
			setUser(null);
		}
	};

	const hasPermission = (
		permission: "view" | "edit" | "admin" | "owner",
	): boolean => {
		if (!user) return false;

		switch (permission) {
			case "view":
				return true; // All roles can view
			case "edit":
				return user.role === "owner" || user.role === "admin";
			case "admin":
				return user.role === "owner" || user.role === "admin";
			case "owner":
				return user.role === "owner";
			default:
				return false;
		}
	};

	return (
		<AuthContext.Provider
			value={{
				user,
				isAuthenticated: !!user,
				isLoading,
				login,
				register,
				logout,
				hasPermission,
			}}
		>
			{children}
		</AuthContext.Provider>
	);
}

// Default SSR-safe context for when AuthProvider isn't available (during SSR)
const defaultAuthContext: AuthContextType = {
	user: null,
	isAuthenticated: false,
	isLoading: true,
	login: async () => false,
	register: async () => ({ success: false, needsEmailConfirmation: false }),
	logout: async () => {},
	hasPermission: () => false,
};

export function useAuth() {
	const context = useContext(AuthContext);
	// Return default context during SSR when provider isn't available yet
	if (!context) {
		return defaultAuthContext;
	}
	return context;
}
